import React, { useState } from 'react';
import PropTypes from 'prop-types';
import capitalize from 'capitalize';
import axios from 'axios';
import { FormattedMessage } from 'react-intl';

import { SecondaryButton } from '../../buttons';
import { Languages } from '../../../tools/languages';

import { useQueryParam } from 'gatsby-query-params';

import './index.scss';

const Input = ({ name, placeholder, optional, id }) => {
  return (
    <label className="form-item half-size">
      <span className="form-item-name" {...(optional && { 'data-optional': 'true' })}>
        {name}
      </span>
      <input
        id={id}
        type="text"
        name={id}
        placeholder={placeholder}
        {...(!optional && { required: 'required' })}
      />
    </label>
  );
};

const Textarea = ({ name, placeholder, optional, id }) => {
  return (
    <label className="form-item">
      <span className="form-item-name" {...(optional && { 'data-optional': 'true' })}>
        {name}
      </span>
      <textarea
        id={id}
        type="text"
        name={id}
        placeholder={placeholder}
        {...(!optional && { required: 'required' })}
      />
    </label>
  );
};

const Select = ({ name, placeholder, optional, id, options }) => {
  return (
    <label className="form-item half-size">
      <span className="form-item-name" {...(optional && { 'data-optional': 'true' })}>
        {name}
      </span>
      <select id={id} name={id} {...(!optional && { required: 'required' })}>
        <option value="">{placeholder}</option>
        {options.map((option) => {
          const value = option.toLowerCase().replace(/\s+/g, '-');
          return (
            <option key={value} value={value}>
              <FormattedMessage id={`contact-${value.toLowerCase()}`} />
            </option>
          );
        })}
      </select>
    </label>
  );
};

const ContactForm = ({ isLoading, language, selectOptions, onSubmit }) => {
  if (language === Languages.NL) {
    return (
      <form id="contact-form" method="post" onSubmit={onSubmit}>
        <Select
          name="Contact"
          placeholder="Kies een melding"
          id="subject"
          options={selectOptions}
        />
        <Input name="Bedrijfsnaam" placeholder="Bavak" id="company" />
        <Input name="Voornaam" placeholder="Voornaam" id="firstName" />
        <Input name="Achternaam" placeholder="Achternaam" id="lastName" />
        <Input name="Email" placeholder="Email" id="email" />
        <Input name="Telefoon" placeholder="Telefoon" id="cellphone" />
        <Textarea name="Bericht" placeholder="Bericht" id="message" optional />
        <div class="g-recaptcha" data-sitekey="6Le7fokqAAAAAKF3ID9XNK0VryOBmEAXSDNL42vm"></div>
        <SecondaryButton>{isLoading ? 'Versturen..' : 'Versturen'}</SecondaryButton>
      </form>
    );
  }

  return (
    <form id="contact-form" method="post" onSubmit={onSubmit}>
      <Select
        name="Contact"
        placeholder="Choose a service type"
        id="subject"
        options={selectOptions}
      />
      <Input name="Company" placeholder="Bavak" id="company" />
      <Input name="First name" placeholder="First name" id="firstName" />
      <Input name="Last name" placeholder="Last name" id="lastName" />
      <Input name="Email" placeholder="Email" id="email" />
      <Input name="Phone" placeholder="Phone" id="cellphone" />
      <Textarea name="Message" placeholder="Message" id="message" optional />
      <div class="g-recaptcha" data-sitekey="6Le7fokqAAAAAKF3ID9XNK0VryOBmEAXSDNL42vm"></div>
      <SecondaryButton>{isLoading ? 'Sending..' : 'Submit'}</SecondaryButton>
    </form>
  );
};

const SentConfirmation = ({ language }) => {
  return (
    <div id="contact-form-sent">
      <svg viewBox="0 0 100 100">
        <style></style>
        <path
          d="M2 50A1 1 0 0 0 98 50A1 1 0 0 0 2 50"
          style={{
            stroke: '#F90C10',
            strokeWidth: '4px',
            fill: 'none',
            animation: 'stroke1 1s',
          }}
        />
        <path
          d="M23 50L42 72L77 34"
          style={{
            stroke: '#110606',
            strokeWidth: '4px',
            fill: 'none',
            animation: 'stroke2 backwards 1s .6s',
          }}
        />
      </svg>
      <span id="contact-form-sent-result">{language === Languages.NL ? 'Verstuurd' : 'Sent'}</span>
      <p id="contact-form-sent-message">
        {language === Languages.NL ? 'Bedankt voor uw bericht.' : 'Thank you for your message.'}
        <br />
        {language === Languages.NL
          ? 'Binnen 24 uur nemen wij contact met u op.'
          : 'We will contact you within 24 hours.'}
      </p>
      <SecondaryButton to="/">
        {language === Languages.NL ? 'Terug naar de homepagina' : 'Back to the homepage'}
      </SecondaryButton>
    </div>
  );
};

const Form = ({ language }) => {
  const selectOptions = ['Service', 'Onderhoud', 'Training', 'Contact', 'Meer informatie'];
  const selectedValue = useQueryParam('subject', 'Contact');
  const solutionType = useQueryParam('solution', '');

  const [sent, setSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (typeof window !== 'undefined') {
    if (selectOptions.includes(capitalize(selectedValue))) {
      const element = document.getElementById('subject');
      if (element) {
        element.value = selectedValue.toLowerCase().replace(/\s+/g, '-');
      }
    }
  }

  const sendContactMessageSentEventToTagManager = () => {
    if (window.dataLayer === undefined) return;
    window.dataLayer.push({
      event: 'contact_message_sent',
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    const subject = document.getElementById('subject').value;
    const company = document.getElementById('company').value;
    const firstName = document.getElementById('firstName').value;
    const lastName = document.getElementById('lastName').value;
    const email = document.getElementById('email').value;
    const cellphone = document.getElementById('cellphone').value;
    const message = document.getElementById('message').value;

    setIsLoading(true);

    const response = await axios.post(process.env.BAVAK_EMAIL_API_URL, {
      firstName: firstName,
      lastName: lastName,
      companyName: company,
      emailAddress: email,
      phoneNumber: cellphone,
      message: message,
      contactType: subject,
      solutionType: solutionType,
      language: language,
    });

    if (response.status === 200) {
      setSent(true);
      sendContactMessageSentEventToTagManager();
    }

    setIsLoading(false);
  };

  return sent ? (
    <SentConfirmation language={language} />
  ) : (
    <ContactForm
      isLoading={isLoading}
      language={language}
      selectOptions={selectOptions}
      onSubmit={onSubmit}
    />
  );
};

Input.propTypes = Textarea.propTypes = {
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  optional: PropTypes.bool,
  id: PropTypes.string.isRequired,
};

Form.propTypes = {
  language: PropTypes.string.isRequired,
};

Form.defaultProps = {
  language: Languages.EN,
};

export default Form;
